// File: src/components/EmbeddedFullPageBanner.jsx
// Auto-redirects embedded users (Shopify or BigCommerce) to the full dashboard
// With option to stay in embedded view

import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../context/AuthContext';

const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL || 'https://baobrain.com';

// Check embedded status synchronously (before any useEffect)
function checkIfEmbedded() {
  const urlParams = new URLSearchParams(window.location.search);
  const host = urlParams.get('host');
  const shop = urlParams.get('shop') || sessionStorage.getItem('shopifyShop');
  const storeHash = urlParams.get('store_hash');
  const isInIframe = window.self !== window.top;
  
  return !!(host || (isInIframe && (shop || storeHash)));
}

export default function EmbeddedFullPageBanner({ onStayEmbedded }) {
  const { token } = useContext(AuthContext);
  // Initialize isEmbedded synchronously to show overlay immediately
  const [isEmbedded] = useState(() => checkIfEmbedded());
  const [externalUrl, setExternalUrl] = useState('');
  const [dismissed, setDismissed] = useState(false);
  const linkRef = useRef(null);

  useEffect(() => {
    if (!isEmbedded || !token) return;
    
    const urlParams = new URLSearchParams(window.location.search);
    let currentPlatform = null;

    // Detect platform
    const host = urlParams.get('host');
    const shop = urlParams.get('shop') || sessionStorage.getItem('shopifyShop');
    const storeHash = urlParams.get('store_hash');
    
    if (host || shop) {
      currentPlatform = 'shopify';
    } else if (storeHash) {
      currentPlatform = 'bigcommerce';
    }

    // Generate the external URL
    const fullPageUrl = new URL('/embedded-login', FRONTEND_URL);
    fullPageUrl.searchParams.set('token', token);
    fullPageUrl.searchParams.set('redirect', '/dashboard');
    
    if (currentPlatform === 'shopify' && shop) {
      fullPageUrl.searchParams.set('shop', shop);
      fullPageUrl.searchParams.set('platform', 'shopify');
    } else if (currentPlatform === 'bigcommerce' && storeHash) {
      fullPageUrl.searchParams.set('store_hash', storeHash);
      fullPageUrl.searchParams.set('platform', 'bigcommerce');
    }
    
    setExternalUrl(fullPageUrl.toString());
  }, [token, isEmbedded]);

  // Auto-click the link once URL is ready (only if not dismissed)
  useEffect(() => {
    if (externalUrl && linkRef.current && !dismissed) {
      const timer = setTimeout(() => {
        linkRef.current?.click();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [externalUrl, dismissed]);

  const handleStayEmbedded = () => {
    setDismissed(true);
    // Store preference for this session
    sessionStorage.setItem('preferEmbedded', 'true');
    // Call parent callback if provided
    if (onStayEmbedded) {
      onStayEmbedded();
    }
  };

  // Not embedded or dismissed = render nothing, let Dashboard show normally
  if (!isEmbedded || dismissed) {
    return null;
  }

  // Full-page takeover for embedded context - shows IMMEDIATELY
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      textAlign: 'center',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      background: 'linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%)',
      zIndex: 9999,
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '40px',
        maxWidth: '420px',
        boxShadow: '0 4px 24px rgba(106, 13, 173, 0.1)',
        border: '1px solid rgba(106, 13, 173, 0.1)',
      }}>
        <div style={{
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #6A0DAD 0%, #00A8A9 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px',
        }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 13V19C18 19.5304 17.7893 20.0391 17.4142 20.4142C17.0391 20.7893 16.5304 21 16 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V8C3 7.46957 3.21071 6.96086 3.58579 6.58579C3.96086 6.21071 4.46957 6 5 6H11" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M15 3H21V9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M10 14L21 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        
        <h2 style={{ 
          color: '#1f2937', 
          marginBottom: '12px', 
          marginTop: 0,
          fontSize: '22px',
          fontWeight: '600'
        }}>
          Opening Full Dashboard
        </h2>
        
        <p style={{ 
          color: '#6b7280', 
          marginBottom: '24px',
          fontSize: '15px',
          lineHeight: '1.5'
        }}>
          For the best experience, we're opening BaoBrain in a new tab.
        </p>
        
        {externalUrl ? (
          <a
            ref={linkRef}
            href={externalUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              width: '100%',
              padding: '14px 24px',
              background: 'linear-gradient(135deg, #6A0DAD 0%, #00A8A9 100%)',
              color: 'white',
              borderRadius: '10px',
              textDecoration: 'none',
              fontWeight: '600',
              fontSize: '16px',
              boxShadow: '0 4px 12px rgba(106, 13, 173, 0.3)',
            }}
          >
            Open Full Dashboard
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </a>
        ) : (
          <div style={{
            width: '32px',
            height: '32px',
            border: '3px solid #e5e5e5',
            borderTop: '3px solid #6A0DAD',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto',
          }} />
        )}
        
        <p style={{ 
          color: '#9ca3af', 
          marginTop: '16px',
          marginBottom: '12px',
          fontSize: '13px'
        }}>
          {externalUrl ? 'Click the button if a new tab doesn\'t open automatically' : 'Preparing redirect...'}
        </p>

        {/* Stay in Shopify option */}
        <button
          onClick={handleStayEmbedded}
          style={{
            background: 'none',
            border: 'none',
            color: '#6b7280',
            fontSize: '13px',
            cursor: 'pointer',
            padding: '8px 16px',
            textDecoration: 'underline',
            marginTop: '4px',
          }}
        >
          Stay here instead
        </button>
      </div>
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );
}